// Loading rangefind's browser bundles.
//
// The bundles are build output copied in by `scripts/sync_engine.mjs` and
// served as static assets. They are loaded by URL at runtime rather than
// imported through the module graph, for two reasons: the transport
// bundle is an order of magnitude larger than the rest and a page that
// never joins a real mesh should not pay for libp2p, and a host that
// serves the engine from elsewhere (a CDN, an app's own asset directory)
// should be able to say so without a rebuild.
//
// Everything below is a hand-written structural type for the parts of the
// engine this product touches. Where a name differs from the protocol
// document, the document wins and the difference is a bug here.

import { ENGINE_BUILD } from "./engine-build.js";
import type { RawArrival, RunPlan, SubscriberStatus, ThreadLink, ThreadUpdate } from "./protocol.js";

export interface LatLon {
  lat: number;
  lon: number;
}

/** The route graph handle (`openRouteGraphUrl`). */
export interface RouteEngine {
  root: { sourceHash: string; profile?: string };
  /** §13 — the inverse of snap(): a segment and a ratio become a place. */
  locate(segment: string, ratio: number): Promise<LatLon>;
  /** §9 — all-pairs times from one shared-context search. Never `itinerary()`. */
  matrix(params: {
    points: LatLon[];
    live?: unknown;
    geometry?: boolean;
  }): Promise<{ seconds: number[][] }>;
  route(params: {
    from: LatLon;
    to: LatLon;
    geometry?: boolean;
    live?: unknown;
  }): Promise<{ seconds: number; geometry?: [number, number][]; distanceMeters?: number }>;
  snap(point: LatLon): Promise<{ matches: { segment: string; ratio: number }[] }>;
  /**
   * Every road in a box, as drawable polylines — the basemap, out of the
   * same static index that does the routing. No tile server, no account,
   * and no way to draw a road the router does not believe in.
   */
  roadsIn(
    bbox: { minLat: number; maxLat: number; minLon: number; maxLon: number },
    options?: { maxLeaves?: number }
  ): Promise<{ roads: { points: [number, number][]; roadClass: string }[]; truncated: boolean }>;
}

/** One follow on the thread channel, from `channel.follow(link)`. */
export interface ThreadFollow {
  latest(): ThreadUpdate | null;
  history(): ThreadUpdate[];
  status(options?: { nowMillis?: number; hasTraffic?: boolean }): SubscriberStatus;
  position(): Promise<(LatLon & { update: ThreadUpdate }) | null>;
  eta(params: {
    plan: RunPlan;
    myStopIndex: number;
    live?: unknown;
    nowMillis?: number;
  }): Promise<RawArrival | null>;
  catchUp(options?: { nowMillis?: number }): Promise<number>;
  /**
   * One sealed proof photo by its commitment (§20.7). Sealed, not open:
   * the key derives from the run's private seed, so only the driver or
   * the dispatcher who minted the ticket can open the result.
   */
  fetchPhoto(hash: string): Promise<Uint8Array | null>;
  /**
   * Every commitment the run has published, recovered from the publisher
   * and checked against the accumulator in the newest record (§20.7.1).
   * Fetch on the strength of `verified` only — `unverified` is what the
   * chain could not vouch for.
   */
  fetchPhotoList(accumulator?: string | null): Promise<{
    matchedAt: number;
    verified: { stopIndex: number; commitment: string }[];
    unverified: { stopIndex: number; commitment: string }[];
  }>;
  stop(): void;
  link: ThreadLink;
}

/** The publishing side of one run, from `channel.publish(...)`. */
export interface ThreadRun {
  link: Uint8Array;
  url: string | null;
  handleFix(fix: {
    lat: number;
    lon: number;
    speedMps?: number;
    nowMillis?: number;
  }): Promise<unknown>;
  markStop(
    index: number,
    outcome: number,
    options?: { reason?: number; note?: string | null; photo?: Uint8Array | null }
  ): Promise<unknown>;
  announce(note: string): Promise<unknown>;
  finish(options?: unknown): Promise<unknown>;
  readonly stopIndex: number;
}

export interface ThreadChannel {
  publish(params: {
    baseUrl?: string;
    mode?: number;
    plan?: RunPlan | null;
    ttlSeconds?: number;
    travelMode?: number;
  }): Promise<ThreadRun>;
  follow(
    linkOrUrl: string | Uint8Array | ThreadLink,
    options?: { onUpdate?: (update: ThreadUpdate | null) => void }
  ): Promise<ThreadFollow>;
  /**
   * Mesh pairing, console side (threads §16). Mints a short-lived offer
   * to show as a QR and collects the replies phones send back.
   *
   * It enrols nobody. `candidates` is a list for a human to compare
   * fingerprints against, and `confirm` is the only thing that tells a
   * device it won — which is the whole point: the mesh replaces the
   * clipboard the card used to travel on, not the judgement at the end.
   */
  startPairing(options?: {
    label?: string;
    addresses?: string[];
  }): Promise<PairingOffer>;
  tick(): Promise<unknown>;
  close(): void;
}

/** A device that answered an offer. A proposal, never an enrolment. */
export interface PairingCandidate {
  publicKeyHex: string;
  name: string;
  /** The eight characters two people read to each other (§16.4). */
  fingerprint: string;
}

export interface PairingOffer {
  /** The offer to render as a QR on the dispatcher's screen. */
  offerBytes: Uint8Array;
  notAfter: number;
  readonly candidates: PairingCandidate[];
  confirm(
    publicKeyHex: string,
    options?: { name?: string }
  ): Promise<PairingCandidate | null>;
  stop(): void;
}

export interface MeshSession {
  node: unknown;
  epoch: string;
  start(): void;
  close(): Promise<void>;
  followRoute?(routes: unknown[]): Promise<unknown>;
  /** The live traffic provider, when this session has one. */
  live?: unknown;
}

interface ThreadsBundle {
  createThreadChannel(options: Record<string, unknown>): ThreadChannel;
  parseBootstrapHint(url: string): string[];
  parseThreadLinkUrl(url: string): ThreadLink;
  decodeThreadLink(bytes: Uint8Array): ThreadLink;
  base64UrlToBytes(text: string): Uint8Array;
  threadLinkUrl(baseUrl: string, link: Uint8Array): string;
  estimateArrival(params: Record<string, unknown>): Promise<RawArrival | null>;
  THREAD_CONSTANTS: Record<string, unknown>;
  THREAD_MODE: { COARSE: number; FINE: number };
  THREAD_STATE: Record<string, number>;
  STOP_OUTCOME: Record<string, number>;
  STOP_REASON: Record<string, number>;
}

interface PulseMeshBundle {
  createLoopbackNetwork(): unknown;
  createMeshSession(options: Record<string, unknown>): Promise<MeshSession>;
  DEFAULT_CONSTANTS: Record<string, unknown>;
}

/**
 * The libp2p transport. A separate bundle and a separate load, because it
 * is an order of magnitude larger than the rest and a page with no peer
 * to dial must not pay for it.
 */
export interface TransportBundle {
  createPulseMeshHost(options: Record<string, unknown>): Promise<MeshHost>;
  createLibp2pNetwork(host: MeshHost): { ready: Promise<unknown> } & Record<string, unknown>;
}

/** The libp2p host, in the two respects this product touches. */
export interface MeshHost {
  peerId: { toString(): string };
  getConnections(): { remotePeer: { toString(): string } }[];
  stop(): Promise<void>;
}

/**
 * A federated engine, opened from a `rangefind-route-catalog-v1` root.
 *
 * Deliberately *not* a `RouteEngine`. A catalogue engine has no `root`,
 * because it is not one graph — it is 256 of them, opened lazily and
 * joined at portals verified against shared OSM node ids. Typing it as a
 * `RouteEngine` would let it flow into `engine.root.sourceHash` and
 * silently produce `undefined` where an epoch belongs, which is the one
 * value that must never be guessed: it is the mesh topic namespace.
 */
export interface RouteCatalogEngine {
  catalog: { indexes: { region: string; profile: string; base: string }[] };
  profile: string;
  route(query: Record<string, unknown>): Promise<Record<string, unknown>>;
  matrix(query: Record<string, unknown>): Promise<Record<string, unknown>>;
  /** Which regional graph covers this point, or undefined off-coverage. */
  regionFor(point: { lat: number; lon: number }): string | undefined;
  clearCaches(): void;
}

interface RouteBundle {
  openRouteGraphUrl(baseUrl: string, options?: Record<string, unknown>): Promise<RouteEngine>;
  openRouteCatalogUrl(
    catalogUrl: string,
    options?: Record<string, unknown>
  ): Promise<RouteCatalogEngine>;
}

export interface EngineBundles {
  route: RouteBundle;
  pulsemesh: PulseMeshBundle;
  threads: ThreadsBundle;
}

const DEFAULT_ENGINE_BASE = "/rangefind/";

/**
 * An absolute URL for the bundle.
 *
 * Absolute on purpose: the bundles live in the app's `public/` directory,
 * and a bundler asked to resolve a root-relative dynamic import into its
 * own public directory refuses — correctly, since those files are copied
 * verbatim and never transformed. A fully-qualified URL is left alone by
 * every bundler and is exactly what the browser wants at runtime.
 *
 * Stamped with the engine's content hash, because the bundles are served
 * `immutable` for a year under names that never change. Without the
 * stamp a browser that has loaded the console once keeps that engine for
 * a year and no fix can reach it — which is not hypothetical: mesh
 * pairing shipped to a console that went on calling an engine with no
 * `startPairing` in it. A new engine is a new URL; an unchanged one keeps
 * its stamp and stays cached, which is the whole point of `immutable`.
 */
export function bundleUrl(base: string, name: string): string {
  const withSlash = String(base).endsWith("/") ? base : `${base}/`;
  const origin = typeof location === "undefined" ? "http://localhost/" : location.href;
  const url = new URL(`${withSlash}${name}`, origin);
  if (ENGINE_BUILD) url.searchParams.set("v", ENGINE_BUILD);
  return url.href;
}

let cached: Promise<EngineBundles> | null = null;

/**
 * Loads the three bundles this product needs. Cached: two apps on one
 * page would otherwise open two copies of the engine, and the epoch
 * arithmetic assumes one.
 */
export function loadEngine(engineBaseUrl: string = DEFAULT_ENGINE_BASE): Promise<EngineBundles> {
  if (cached) return cached;
  cached = (async () => {
    const [route, pulsemesh, threads] = await Promise.all([
      import(/* @vite-ignore */ bundleUrl(engineBaseUrl, "route.browser.js")) as Promise<RouteBundle>,
      import(/* @vite-ignore */ bundleUrl(engineBaseUrl, "pulsemesh.browser.js")) as Promise<PulseMeshBundle>,
      import(/* @vite-ignore */ bundleUrl(engineBaseUrl, "pulsemesh-threads.browser.js")) as Promise<ThreadsBundle>
    ]);
    return { route, pulsemesh, threads };
  })();
  return cached;
}

let transportCached: Promise<TransportBundle> | null = null;

/**
 * Loads the transport bundle. Called **only** when the page actually has
 * a peer to dial, which is the same condition the Android engine host
 * uses (`packages/engine-host/bridge.js`) before importing the same file.
 */
export function loadTransport(engineBaseUrl: string = DEFAULT_ENGINE_BASE): Promise<TransportBundle> {
  if (!transportCached) {
    transportCached = import(
      /* @vite-ignore */ bundleUrl(engineBaseUrl, "pulsemesh-libp2p.browser.js")
    ) as Promise<TransportBundle>;
  }
  return transportCached;
}

/** For tests and for a host that wants a second, isolated engine. */
export function resetEngineCache(): void {
  cached = null;
  transportCached = null;
}
